import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Manage admin's Google OAuth connection (status, connect, disconnect)
 * and fetch Google Calendar events for the current week.
 *
 * Returns:
 *   - status, events
 *   - connect()  : redirects to Google consent screen
 *   - disconnect()
 *   - refresh(start, end) : re-fetch status + events for a given range
 */
export const useGoogleSync = (enabled, weekStartStr, weekEndStr) => {
  const [status, setStatus] = useState({ connected: false, google_email: '' });
  const [events, setEvents] = useState([]);

  const refresh = useCallback(async (start, end) => {
    if (!enabled || !start || !end) return;
    try {
      const [s, e] = await Promise.all([
        axios.get(`${API_URL}/api/oauth/google/status`, { withCredentials: true }).catch(() => ({ data: { connected: false } })),
        axios.get(`${API_URL}/api/oauth/google/events?start_date=${start}&end_date=${end}`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setStatus(s.data || { connected: false });
      setEvents(e.data || []);
    } catch { /* silent */ }
  }, [enabled]);

  useEffect(() => { refresh(weekStartStr, weekEndStr); }, [refresh, weekStartStr, weekEndStr]);

  // Pick up the OAuth callback redirect (?google_connected=1 / ?google_error=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google_connected') === '1') {
      toast.success('Google Agenda connecte');
      window.history.replaceState({}, document.title, window.location.pathname);
      refresh(weekStartStr, weekEndStr);
    } else if (params.get('google_error')) {
      toast.error(`Erreur Google: ${params.get('google_error')}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // Run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/oauth/google/authorize`, { withCredentials: true });
      window.location.href = res.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur connexion Google');
    }
  }, []);

  const disconnect = useCallback(async () => {
    if (!window.confirm('Deconnecter Google Agenda ?')) return;
    try {
      await axios.delete(`${API_URL}/api/oauth/google/disconnect`, { withCredentials: true });
      toast.success('Google Agenda deconnecte');
      setStatus({ connected: false });
      setEvents([]);
    } catch {
      toast.error('Erreur deconnexion');
    }
  }, []);

  return { status, events, connect, disconnect, refresh };
};
